import React, { useState } from "react";

import { useChatInteract } from "@chainlit/react-client";

import { Translator } from "@/components/i18n";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { EditSquare } from "../icons/EditSquare";
import { useSidebar } from "../ui/sidebar";
import { boolean } from "zod";
import { useCarstenTheme } from "../LeftSidebar/ThreadList";

type NewChatDialogProps = {
  open: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
};

export const NewChatDialog = ({
  open,
  handleClose,
  handleConfirm,
}: NewChatDialogProps) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    event.preventDefault();
    if (event.key === "Enter") {
      handleConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        id="new-chat-dialog"
        className="sm:max-w-md"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <DialogTitle>
            <Translator
              style={{
                fontSize: "16px",
                fontWeight: "600",
                lineHeight: "24px",
              }}
              path="navigation.newChat.dialog.title"
            />
          </DialogTitle>
          <DialogDescription>
            <Translator
              style={{
                fontSize: "16px",
                fontWeight: "400",
                lineHeight: "24px",
              }}
              path="navigation.newChat.dialog.description"
            />
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 h- sm:gap-0">
          <Button
            variant="outline"
            style={{
              backgroundColor: "#fff",

              border: "3px solid #01FF48",
            }}
            onClick={handleClose}
          >
            <Translator
              style={{ color: "#000", fontWeight: "bold" }}
              path="common.actions.cancel"
            />
          </Button>
          <Button
            variant="outline"
            style={{
              backgroundColor: "#01FF48",
            }}
            onClick={handleConfirm}
            id="confirm"
          >
            <Translator
              style={{ color: "#000", fontWeight: "bold" }}
              path="common.actions.confirm"
            />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  navigate?: (to: string) => void;
  onConfirm?: () => void;
  label?: boolean;
}
const NewChatButton = (
  { navigate, label = false, onConfirm, className, ...buttonProps }: Props,
) => {
  const [open, setOpen] = useState(false);
  const { clear } = useChatInteract();
  const sidebar = useSidebar();
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    } else {
      clear();
      navigate?.("/");
    }
    handleClose();
  };
  const CarstenStyles = useCarstenTheme()
  return (
    <div>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              id="new-chat-button"
              className={`
    flex items-center justify-start
     w-full h-11
 
    rounded-sm
    ${CarstenStyles.sidebar.background.default} 
    ${CarstenStyles.sidebar.background.hover}
    ${className}
  `}
              onClick={handleClickOpen}
              {...buttonProps}
            >
              <span className={`
    flex-shrink-0
    rounded flex justify-center items-center w-[44px] h-[44px]
    ${CarstenStyles.sidebar.icon.default} 
    ${CarstenStyles.sidebar.icon.hover}
  `}>
                <EditSquare />
              </span>

              {label && (
                <Translator
                  className={`
        ml-2
        whitespace-nowrap
        ${CarstenStyles.sidebar.text.active} 
        ${CarstenStyles.sidebar.text.default} 
        ${CarstenStyles.sidebar.text.hover}
      `}
                  path="navigation.newChat.dialog.tooltip"
                />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <Translator path="navigation.newChat.dialog.tooltip" />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <NewChatDialog
        open={open}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
      />
    </div>
  );
};

export default NewChatButton;
